export interface HederaExtensionData {
  id: string;
  name: string;
  icon: string;
}

enum EVENTS {
  extensionQuery = "hedera-extension-query",
  extensionConnect = "hedera-extension-connect-",
  extensionOpen = "hedera-extension-open-",
  extensionResponse = "hedera-extension-response",
}

/**
 * Helper class to interact with WalletConnect-compatible Hedera browser extensions.
 *
 * The class is responsible for:
 * - Finding available extensions
 * - Validating extension compatibility
 * - Connecting to the extension
 * - Opening the extension popup for signing upon request
 */
export abstract class HederaExtensionHelper {
  static findExtensions(): Promise<HederaExtensionData[]> {
    return new Promise((resolve) => {
      if (typeof window === "undefined") {
        resolve([]);
        return;
      }

      const extensions: HederaExtensionData[] = [];

      const updateExtensions = (metadata: HederaExtensionData) => {
        const existingIndex = extensions.findIndex((f) => f.id === metadata.id);

        if (existingIndex !== -1) {
          extensions[existingIndex] = metadata;
        } else {
          extensions.push(metadata);
        }
      };

      const onMessage = (event: MessageEvent) => {
        if (event?.data?.type === EVENTS.extensionResponse) {
          if (this.validateExtension(event.data.metadata)) {
            updateExtensions(event.data.metadata);
          }
        }
      };

      window.addEventListener("message", onMessage);

      this.extensionQuery();

      setTimeout(() => {
        window.removeEventListener("message", onMessage);
        resolve(extensions);
      }, 200);
    });
  }

  static extensionConnect(id: string, pairingString: string) {
    window.postMessage(
      { type: EVENTS.extensionConnect + id, pairingString },
      "*"
    );
  }

  static extensionOpen(id: string) {
    window.postMessage({ type: EVENTS.extensionOpen + id }, "*");
  }

  static extensionQuery() {
    window.postMessage({ type: EVENTS.extensionQuery }, "*");
  }

  static validateExtension(extension: any): extension is HederaExtensionData {
    try {
      return (
        extension &&
        typeof extension === "object" &&
        typeof extension.id === "string" &&
        typeof extension.name === "string" &&
        typeof extension.icon === "string"
      );
    } catch {
      return false;
    }
  }
}

/**
 * Parses a Hedera account ID to an EVM address.
 *
 * Hedera Account IDs are in the format of `<shard>.<realm>.<account-number>`.
 * Example: `0.0.1234`:
 * - Shard: `0`
 * - Realm: `0`
 * - Account number: `1234`
 *
 * We can derive an EVM-compatible address of a Hedera account ID
 * by hex-encoding the account number and prefixing it with zeros up to 40 characters.
 *
 * This is called "account-num alias" and can be used to interact
 * with the Hedera network using standard EVM wallets.
 *
 * @see https://hips.hedera.com/hip/hip-583
 */
export function convertHederaAccountIdToEvmAddress(accId: string): string {
  return "0x" + Number(accId.split(".").at(-1)).toString(16).padStart(40, "0");
}

/**
 * Converts an EVM address (account-num alias) to a Hedera account ID.
 *
 * Follows the inverse logic of {@link convertHederaAccountIdToEvmAddress}
 * by converting the hex-encoded account number to a decimal number
 */
export function convertEvmAddressToHederaAccountId(address: string): string {
  return "0.0." + Number(address);
}

/**
 * In Hedera, native HBAR has 8 decimals, but when handling `value` or gas in the EVM we need to use 18 decimals.
 * To handle this we need to multiply/divide the 1e8 HBAR by 10^10 to get the scaled 1e18/1e8 HBAR value.
 * @see https://docs.hedera.com/hedera/core-concepts/smart-contracts/understanding-hederas-evm-differences-and-compatibility/for-evm-developers-migrating-to-hedera/decimal-handling-8-vs.-18-decimals
 */
const HBAR_SCALING_FACTOR = BigInt(10 ** 10);

export function scaleHbarToWei(hbar: bigint): bigint {
  return hbar * HBAR_SCALING_FACTOR;
}

export function scaleWeiToHbar(wei: bigint): bigint {
  return wei / HBAR_SCALING_FACTOR;
}
